import { z } from "zod";
import type { Operation } from "../types";

// Local-runtime discovery (docs/models/PRD.md §14 "use local Ollama", M1). The
// route calls `discoverLocalProviders()` from `@excellent/core` optimistically —
// that module lands on a parallel workstream, so the route falls back to an empty
// list when it isn't present yet.

export const discoveredProvider = z.object({
  id: z.string(),
  label: z.string(),
  baseUrl: z.string(),
  /** Model ids the local runtime reports as installed, if any. */
  models: z.array(z.string()),
  reachable: z.boolean(),
});
export type DiscoveredProvider = z.infer<typeof discoveredProvider>;

const discoverResponse = z.object({
  providers: z.array(discoveredProvider),
  /** True when the discovery module was present (vs the empty fallback). */
  available: z.boolean(),
});
export type ModelsDiscoverResponse = z.infer<typeof discoverResponse>;

const get: Operation<void, void, void, ModelsDiscoverResponse> = {
  method: "GET",
  path: () => "/api/models/discover",
  response: discoverResponse,
};

// ── Native local-model lifecycle (LM-A): catalog + residency + headroom ──────

export const catalogTier = z.object({
  tier: z.string(),
  model: z.string(),
  paramClass: z.string(),
  approxBytes: z.number(),
  alternatives: z.array(z.string()),
});
export type CatalogTier = z.infer<typeof catalogTier>;

export const residentModel = z.object({
  name: z.string(),
  sizeBytes: z.number().nullable(),
  running: z.boolean(),
});
export type ResidentModel = z.infer<typeof residentModel>;

const localStatusResponse = z.object({
  reachable: z.boolean(),
  catalog: z.array(catalogTier),
  resident: z.array(residentModel),
  headroomBytes: z.number(),
  residency: z.object({
    singleResident: z.boolean().optional(),
    maxDiskBytes: z.number().optional(),
  }),
});
export type ModelsLocalStatusResponse = z.infer<typeof localStatusResponse>;

const local: Operation<void, void, void, ModelsLocalStatusResponse> = {
  method: "GET",
  path: () => "/api/models/local",
  response: localStatusResponse,
};

const lifecycleBody = z.object({ model: z.string().min(1) });
const lifecycleResponse = z.object({
  ok: z.boolean(),
  evicted: z.array(z.string()).optional(),
  reason: z.string().optional(),
});
export type ModelsLifecycleResponse = z.infer<typeof lifecycleResponse>;

const install: Operation<void, void, z.infer<typeof lifecycleBody>, ModelsLifecycleResponse> = {
  method: "POST",
  path: () => "/api/models/local/install",
  body: lifecycleBody,
  response: lifecycleResponse,
};

const remove: Operation<void, void, z.infer<typeof lifecycleBody>, ModelsLifecycleResponse> = {
  method: "POST",
  path: () => "/api/models/local/remove",
  body: lifecycleBody,
  response: lifecycleResponse,
};

export const modelsDiscover = { get, local, install, remove };
